/**
 * The "Check now" toast for the SEO Co-Pilot index queue.
 *
 * A forced run is bounded (25 s, a 3-URL Bing slice, no link scan — CLAUDE.md
 * §12l) and Google only re-inspects a URL every 2 days, so "0 checked,
 * 0 indexed" is the NORMAL answer on a healthy, recently-checked site. The
 * toast therefore says what the run actually did — sitemaps re-submitted,
 * Bing checks and submits, IndexNow pings — and why Google's count is 0,
 * instead of three zeros that read as "nothing happened".
 */
export interface IndexQueueRunResult {
  enqueued: number;
  inspected: number;
  indexed: number;
  errors: number;
  sitemapsSubmitted?: number;
  indexNowUrls?: number;
  bingChecked?: number;
  bingIndexed?: number;
  bingSubmitted?: number;
  bingErrors?: number;
  firstError?: string;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** One line for `toast.success`, and the first error for a `toast.warning` when any step failed. */
export function describeIndexQueueRun(d: IndexQueueRunResult): {
  summary: string;
  warning: string | null;
} {
  const parts: string[] = [];
  if (d.enqueued > 0) parts.push(`+${plural(d.enqueued, 'new URL')}`);
  if ((d.sitemapsSubmitted ?? 0) > 0) {
    parts.push(`${plural(d.sitemapsSubmitted!, 'sitemap')} re-submitted to Google`);
  }
  if (d.inspected > 0) {
    parts.push(`Google: ${d.inspected} checked, ${d.indexed} indexed`);
  } else {
    parts.push('Google: nothing due for a check (every URL was checked in the last 2 days)');
  }
  if ((d.bingChecked ?? 0) > 0 || (d.bingSubmitted ?? 0) > 0) {
    const bing = [`${d.bingChecked ?? 0} checked`];
    if ((d.bingIndexed ?? 0) > 0) bing.push(`${d.bingIndexed} indexed`);
    if ((d.bingSubmitted ?? 0) > 0) bing.push(`${d.bingSubmitted} submitted`);
    parts.push(`Bing: ${bing.join(', ')}`);
  }
  if ((d.indexNowUrls ?? 0) > 0) parts.push(`IndexNow: ${plural(d.indexNowUrls!, 'URL')} pinged`);
  const failures = d.errors + (d.bingErrors ?? 0);
  const warning =
    failures > 0
      ? `${plural(failures, 'step')} failed${d.firstError ? `: ${d.firstError}` : ''}`
      : null;
  return { summary: `Queue run — ${parts.join(' · ')}`, warning };
}
